/**
 * タレント個別通知ルール（Layer 2）の API hooks
 */
import { useCallback, useEffect, useState } from "react";
import { RestApiError, restClient } from "../lib/rest-client";

/**
 * イベントカテゴリ
 */
export type TalentNotifyRuleCategory =
  | "pipeline"
  | "contract"
  | "matter"
  | "talent";

/**
 * 個別通知ルール
 */
export type TalentNotifyRuleItem = {
  eventType: string;
  category: TalentNotifyRuleCategory;
  label: string;
  slackEnabled: boolean;
  emailEnabled: boolean;
  isEnabled: boolean;
  layer2Supported: boolean;
};

/**
 * 通知ルール一覧レスポンス
 */
export type TalentNotifyRules = {
  talentId: string;
  slackChannelId: string | null;
  slackChannelName: string | null;
  notificationEmail: string | null;
  talentEmail: string | null;
  partnerContact: string | null;
  slackConnected: boolean;
  /**
   * notification_email がバウンスした日時。設定されている場合、メール通知は自動でOFFにされている。
   */
  emailBounceAt: string | null;
  rules: TalentNotifyRuleItem[];
  updatedAt: string;
};

/**
 * 個別ルールの更新入力
 */
export type TalentNotifyRuleInput = {
  eventType: string;
  slackEnabled: boolean;
  emailEnabled: boolean;
  isEnabled: boolean;
};

/**
 * 一括更新リクエスト
 */
export type UpdateTalentNotifyRulesRequest = {
  updateDestinations?: boolean;
  slackChannelId?: string | null;
  slackChannelName?: string | null;
  notificationEmail?: string | null;
  rules?: TalentNotifyRuleInput[];
};

/**
 * テスト通知の結果
 */
export type TestNotificationResult = {
  success: boolean;
  channel: string;
  errorMessage: string | null;
};

/**
 * タレントの通知ルールを取得する hook
 * @param {string} talentId タレントID
 * @return {object} data, loading, error, refetch
 */
export function useTalentNotifyRules(talentId: string | null): {
  data: TalentNotifyRules | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [data, setData] = useState<TalentNotifyRules | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchRules = useCallback(async (): Promise<void> => {
    if (!talentId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.get<TalentNotifyRules>(
        `/talents/${talentId}/notify-rules`,
      );
      setData(response);
    } catch (e) {
      if (e instanceof RestApiError) {
        setError(e);
      } else {
        setError(
          new RestApiError(
            e instanceof Error ? e.message : "Unknown error",
            500,
          ),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [talentId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  return { data, loading, error, refetch: fetchRules };
}

/**
 * タレントの通知ルールを一括更新する hook
 * @return {object} update, loading
 */
export function useUpdateTalentNotifyRules(): {
  update: (
    talentId: string,
    request: UpdateTalentNotifyRulesRequest,
  ) => Promise<TalentNotifyRules>;
  loading: boolean;
} {
  const [loading, setLoading] = useState(false);

  const update = useCallback(
    async (
      talentId: string,
      request: UpdateTalentNotifyRulesRequest,
    ): Promise<TalentNotifyRules> => {
      setLoading(true);
      try {
        return await restClient.put<TalentNotifyRules>(
          `/talents/${talentId}/notify-rules`,
          request,
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { update, loading };
}

/**
 * タレント通知ルールを個別にPATCHする hook
 * @return {object} patchRule, loading
 */
export function usePatchTalentNotifyRule(): {
  patchRule: (
    talentId: string,
    eventType: string,
    fields: Partial<
      Pick<TalentNotifyRuleInput, "slackEnabled" | "emailEnabled" | "isEnabled">
    >,
  ) => Promise<TalentNotifyRules>;
  loading: boolean;
} {
  const [loading, setLoading] = useState(false);

  const patchRule = useCallback(
    async (
      talentId: string,
      eventType: string,
      fields: Partial<
        Pick<
          TalentNotifyRuleInput,
          "slackEnabled" | "emailEnabled" | "isEnabled"
        >
      >,
    ): Promise<TalentNotifyRules> => {
      setLoading(true);
      try {
        return await restClient.patch<TalentNotifyRules>(
          `/talents/${talentId}/notify-rules/${eventType}`,
          fields,
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { patchRule, loading };
}

/**
 * タレント通知ルールをデフォルトにリセットする hook
 * @return {object} reset, loading
 */
export function useResetTalentNotifyRules(): {
  reset: (talentId: string) => Promise<TalentNotifyRules>;
  loading: boolean;
} {
  const [loading, setLoading] = useState(false);

  const reset = useCallback(
    async (talentId: string): Promise<TalentNotifyRules> => {
      setLoading(true);
      try {
        return await restClient.post<TalentNotifyRules>(
          `/talents/${talentId}/notify-rules/reset`,
          {},
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { reset, loading };
}

/**
 * テスト通知を送信する hook
 * @return {object} sendTest, loading
 */
export function useTestTalentNotification(): {
  sendTest: (
    talentId: string,
    channel: "slack" | "email",
    eventType?: string,
  ) => Promise<TestNotificationResult>;
  loading: boolean;
} {
  const [loading, setLoading] = useState(false);

  const sendTest = useCallback(
    async (
      talentId: string,
      channel: "slack" | "email",
      eventType = "pipeline.won",
    ): Promise<TestNotificationResult> => {
      setLoading(true);
      try {
        return await restClient.post<TestNotificationResult>(
          `/talents/${talentId}/notify-rules/test`,
          { channel, eventType },
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { sendTest, loading };
}
